import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api, apiError, API, fmtDate } from "../lib/api";
import { toast } from "sonner";
import { Download, FileText } from "lucide-react";

const cur_month_ist = () => {
    const now = new Date();
    const ist = new Date(now.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
    return `${ist.getFullYear()}-${String(ist.getMonth() + 1).padStart(2, "0")}`;
};

export default function ExpenseMonthReportPage() {
    const { siteId } = useParams();
    const [params, setParams] = useSearchParams();
    const [month, setMonth] = useState(params.get("month") || cur_month_ist());
    const [rep, setRep] = useState(null);
    const [loading, setLoading] = useState(true);

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get(`/sites/${siteId}/expenses/monthly-report`, { params: { month } });
            setRep(data);
        } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
    }
    useEffect(() => { setParams({ month }); load(); // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [month]);

    const tok = localStorage.getItem("fx_token");
    async function downloadFile(url, filename) {
        const res = await fetch(`${API}${url}`, { headers: { Authorization: `Bearer ${tok}` } });
        const blob = await res.blob();
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob); a.download = filename; a.click();
    }

    return (
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-6 sm:py-8">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <div className="text-xs uppercase tracking-wider text-gray-500">Monthly report · {rep?.site?.name || "…"}</div>
                    <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1E3355] mt-1">Expenses — {month}</h1>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="rounded-md border border-gray-200 px-3 py-2 text-sm" data-testid="report-month-picker" />
                    <button onClick={() => downloadFile(`/sites/${siteId}/expenses/monthly-report/export.csv?month=${month}`, `expenses-${month}.csv`)}
                        className="rounded-md bg-white border border-gray-200 px-3 py-2 text-sm inline-flex items-center gap-1 hover:bg-gray-50" data-testid="report-export-csv">
                        <Download className="w-4 h-4" /> CSV
                    </button>
                    <button onClick={() => downloadFile(`/sites/${siteId}/expenses/monthly-report/export.pdf?month=${month}`, `expenses-${month}.pdf`)}
                        className="rounded-md bg-[#1E3355] text-white px-3 py-2 text-sm inline-flex items-center gap-1 hover:opacity-90" data-testid="report-export-pdf">
                        <Download className="w-4 h-4" /> PDF
                    </button>
                </div>
            </div>
            {loading || !rep ? (
                <div className="mt-8 text-center text-gray-400">Loading…</div>
            ) : (
                <>
                    <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <Stat label="Entries" value={rep.count_entries} tid="rep-count" />
                        <Stat label="Total ₹" value={Number(rep.grand_total).toLocaleString("en-IN")} tid="rep-total" />
                        <Stat label="Missing invoice" value={rep.missing_invoice_count} tone={rep.missing_invoice_count ? "warn" : "ok"} tid="rep-missing" />
                        <Stat label="Locked?" value={rep.locked ? "Yes" : "No"} tid="rep-locked" />
                    </div>

                    <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {Object.entries(rep.group_totals).map(([g, v]) => (
                            <div key={g} className="rounded-xl bg-white border border-gray-100 p-4" data-testid={`group-${g}`}>
                                <div className="text-[10px] uppercase tracking-wider text-gray-500">{g.replace("_", " ")}</div>
                                <div className="mt-1 text-lg font-bold text-[#1E3355]">₹ {Number(v).toLocaleString("en-IN")}</div>
                            </div>
                        ))}
                    </div>

                    {rep.categories.map((c) => (
                        <div key={c.category} className="mt-6 rounded-xl bg-white border border-gray-100 overflow-hidden" data-testid={`report-cat-${c.category}`}>
                            <div className="px-4 py-2 bg-[#F4F6F9] flex items-center justify-between">
                                <div className="font-semibold text-[#1E3355]">{c.category} <span className="text-xs text-gray-500 ml-2">{c.group}</span></div>
                                <div className="text-sm font-bold text-[#1E3355]">₹ {Number(c.subtotal).toLocaleString("en-IN")}</div>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="min-w-full text-sm">
                                    <thead className="text-xs text-gray-500 uppercase tracking-wider">
                                        <tr><th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-left">Vendor</th><th className="px-3 py-2 text-left">Invoice #</th><th className="px-3 py-2 text-left">Description</th><th className="px-3 py-2 text-right">Amount ₹</th><th className="px-3 py-2 text-left">Flags</th></tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {c.lines.map((e) => (
                                            <tr key={e.id}>
                                                <td className="px-3 py-2">{fmtDate(e.date)}</td>
                                                <td className="px-3 py-2 font-medium">{e.vendor_name}</td>
                                                <td className="px-3 py-2">{e.invoice_number || "—"}</td>
                                                <td className="px-3 py-2 max-w-[260px] truncate">{e.description}</td>
                                                <td className="px-3 py-2 text-right tabular-nums">{Number(e.amount).toLocaleString("en-IN")}</td>
                                                <td className="px-3 py-2 text-xs">
                                                    {e.is_recoverable && <span className="inline-block px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 mr-1">Rec</span>}
                                                    {e.invoice_pending && <span className="inline-block px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 mr-1">Pending</span>}
                                                    {e.source === "import" && <span className="inline-block px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 mr-1">Imp</span>}
                                                    {e.invoice_file_id && <a href={`${API}/expense-invoice-files/${e.invoice_file_id}/blob`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-[#1E3355] hover:underline"><FileText className="w-3 h-3 mr-0.5" />invoice</a>}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ))}
                </>
            )}
        </div>
    );
}
function Stat({ label, value, tone = "ok", tid }) {
    const cls = tone === "warn" ? "bg-amber-50 border-amber-100 text-amber-700" : "bg-white border-gray-100 text-[#1E3355]";
    return <div className={`rounded-xl border p-4 ${cls}`} data-testid={tid}><div className="text-xs uppercase tracking-wider text-gray-500">{label}</div><div className="mt-1 text-xl font-bold">{value}</div></div>;
}
